/**
 * The ONLY file that imports `nepali-date-converter`. Everything else in this
 * codebase depends on `./bikramSambat.ts` instead — if the underlying
 * package ever needs replacing, this is the one seam that changes.
 *
 * The library supports Bikram Sambat years roughly 2000-2090 (see its
 * `dateConfigMap`), comfortably covering current and near-future bills.
 */
import NepaliDate from 'nepali-date-converter';

export interface EngineDate {
  year: number;
  /** 1-12 (Baisakh=1 .. Chaitra=12 for BS, January=1 .. December=12 for AD) */
  month: number;
  day: number;
}

/**
 * Builds a JS Date anchored at local noon rather than midnight. NepaliDate's
 * internal conversion bakes in a fixed +5:45 (Kathmandu) offset; anchoring at
 * noon gives a wide safety margin against any local-timezone rounding ever
 * shifting the resulting calendar date by a day in either direction. Verified
 * against known Bikram Sambat New Year reference dates in bikramSambat.test.ts.
 */
function noonLocalDate(year: number, month1to12: number, day: number): Date {
  return new Date(year, month1to12 - 1, day, 12, 0, 0, 0);
}

export function engineAdToBs(ad: EngineDate): EngineDate {
  const jsDate = noonLocalDate(ad.year, ad.month, ad.day);
  const nepaliDate = NepaliDate.fromAD(jsDate);
  const bs = nepaliDate.getBS();
  return { year: bs.year, month: bs.month + 1, day: bs.date };
}

export function engineBsToAd(bs: EngineDate): EngineDate {
  // NepaliDate's (year, monthIndex, date) constructor takes BS components
  // directly (monthIndex is 0-11) and converts internally — no Date needed in.
  const nepaliDate = new NepaliDate(bs.year, bs.month - 1, bs.day);
  const jsDate = nepaliDate.toJsDate();
  return { year: jsDate.getFullYear(), month: jsDate.getMonth() + 1, day: jsDate.getDate() };
}

export function engineTodayBs(): EngineDate {
  const now = new Date();
  return engineAdToBs({ year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() });
}

const BS_MONTH_NAMES_EN = [
  'Baisakh', 'Jestha', 'Asar', 'Shrawan', 'Bhadra', 'Aswin',
  'Kartik', 'Mangsir', 'Poush', 'Magh', 'Falgun', 'Chaitra',
];
const BS_MONTH_NAMES_NE = [
  'बैशाख', 'जेठ', 'असार', 'श्रावण', 'भाद्र', 'आश्विन',
  'कार्तिक', 'मंसिर', 'पौष', 'माघ', 'फाल्गुण', 'चैत्र',
];

export function engineFormatBs(bs: EngineDate, locale: 'en' | 'ne'): string {
  const names = locale === 'ne' ? BS_MONTH_NAMES_NE : BS_MONTH_NAMES_EN;
  const name = names[bs.month - 1];
  return `${name} ${bs.day}, ${bs.year}`;
}
